import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { FaUser, FaEnvelope, FaTag, FaComment, FaBug, FaDownload } from "react-icons/fa";
import Logo from "../components/Logo";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import LoadingIndicator from "../components/LoadingIndicator";
import "../styles/ReportBug.css";

function ReportBug() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        category: "",
        message: ""
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const bugCategories = [
        { value: "user_interface", label: "User Interface" },
        { value: "chat_ui", label: "Chat UI" },
        { value: "agent", label: "Agent" },
        { value: "communication", label: "Communication" },
        { value: "authentication", label: "Authentication" },
        { value: "performance", label: "Performance" },
        { value: "other", label: "Other" }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };



    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");

        // Validate form
        if (!formData.name.trim() || !formData.email.trim() || !formData.category || !formData.message.trim()) {
            setError("Please fill in all required fields");
            setLoading(false);
            return;
        }

        // Execute reCAPTCHA v3
        if (!executeRecaptcha) {
            setError("reCAPTCHA not ready. Please try again.");
            setLoading(false);
            return;
        }

        const recaptchaToken = await executeRecaptcha('submit_bug_report');
        if (!recaptchaToken) {
            setError("reCAPTCHA verification failed. Please try again.");
            setLoading(false);
            return;
        }

        try {
            const response = await api.post("/api/bug-report/", {
                ...formData,
                recaptcha: recaptchaToken
            });

            if (response.data.success) {
                setSuccess(true);
                setFormData({
                    name: "",
                    email: "",
                    category: "",
                    message: ""
                });

            }
        } catch (error) {
            if (error.response?.data) {
                const errorData = error.response.data;
                if (typeof errorData === 'object') {
                    const errorMessages = [];
                    Object.keys(errorData).forEach(field => {
                        if (Array.isArray(errorData[field])) {
                            errorMessages.push(...errorData[field]);
                        } else {
                            errorMessages.push(errorData[field]);
                        }
                    });
                    setError(errorMessages.join(' '));
                } else {
                    setError(errorData);
                }
            } else {
                setError("An error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="report-bug-container">
                <header className="report-bug-header">
                    <Logo size="small" showText={false} linkTo="/" />
                    <nav className="nav-links">
                        <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
                        <Link to="/login" className="login-link">Login</Link>
                        <Link to="/register" className="register-link">Register</Link>
                    </nav>
                </header>

                <div className="report-bug-content">
                    <div className="success-card">
                        <div className="success-icon">
                            <FaBug />
                        </div>
                        <h2>Bug Report Submitted Successfully!</h2>
                        <p>Thank you for reporting this bug. We'll review it and get back to you if needed.</p>
                        <div className="success-actions">
                            <Link to="/" className="btn btn-primary">Go Home</Link>
                            <button 
                                onClick={() => setSuccess(false)} 
                                className="btn btn-outline"
                            >
                                Report Another Bug
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="report-bug-container">
            <header className="report-bug-header">
                <Logo size="small" showText={false} linkTo="/" />
                <nav className="nav-links">
                    <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
                    <Link to="/login" className="login-link">Login</Link>
                    <Link to="/register" className="register-link">Register</Link>
                </nav>
            </header>

            <div className="report-bug-content">
                <div className="report-bug-card">
                    <div className="report-bug-header-content">
                        <FaBug className="bug-icon" />
                        <h2>Report a Bug</h2>
                        <p>Help us improve triagz by reporting any bugs you encounter</p>
                    </div>

                    <form onSubmit={handleSubmit} className="report-bug-form">
                        {error && <div className="error-message">{error}</div>}

                        <div className="form-group">
                            <label htmlFor="name">Name <span className="required">*</span></label>
                            <div className="input-group">
                                <FaUser className="input-icon" />
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter your name"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email Address <span className="required">*</span></label>
                            <div className="input-group">
                                <FaEnvelope className="input-icon" />
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter your email address"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="category">Bug Category <span className="required">*</span></label>
                            <div className="input-group">
                                <FaTag className="input-icon" />
                                <select
                                    id="category"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select a category</option>
                                    {bugCategories.map(category => (
                                        <option key={category.value} value={category.value}>
                                            {category.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="message">Bug Description <span className="required">*</span></label>
                            <div className="input-group">
                                <FaComment className="input-icon" />
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder="Please describe the bug in detail. Include steps to reproduce, expected behavior, and actual behavior."
                                    rows={5}
                                    required
                                />
                            </div>
                        </div>

                        <button 
                            className="submit-button" 
                            type="submit" 
                            disabled={loading}
                        >
                            {loading ? <LoadingIndicator /> : "Submit Bug Report"}
                        </button>
                    </form>

                    <div className="form-footer">
                        <Link to="/">← Back to Home</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReportBug; 